import {
  ArsTable,
  ArsTableBody,
  ArsTableRow,
  IRequestStatus,
  isArsTableBody,
  loadWrapper,
  PauseCover,
} from "aic";
import { CommentScope } from "components/common/comments/CommentScope";
import { v3EditorProps } from "components/common/v3EditorProps";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { TlfContext } from "contexts/TlfContext";
import { IEditorV3, isIEditorV3 } from "editor-v3";
import { eAccessRights } from "functions/api/enums";
import { getArsTableCalculation } from "functions/api/tlfItem/getArsTableCalculation";
import { isTlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { useContext, useEffect, useEffectEvent, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { ArsSetPicker } from "./ArsSetPicker";

/**
 * Wrapper component for ARSTable.
 */
export const TlfArsTable = () => {
  // Contexts
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const tlfContext = useContext(TlfContext);
  const tlfData = tlfContext?.tlfData;
  // Derived from contexts
  const body =
    tlfContext && isTlfItemReadDto(tlfData) && isArsTableBody(tlfData.body, isIEditorV3)
      ? tlfData.body
      : null;
  const disabled = !hierarchyContext?.accessRights.includes(
    eAccessRights.WriteToTlfItemsInHierarchy,
  );
  const isLoading = hierarchyContext?.isClinicalLibraryLoading ?? true;

  // Component state items
  const [calculatedTableRows, setCalculatedTableRows] = useState<ArsTableRow<IEditorV3>[]>([]);
  const [requestingScaffoldData, setRequestingScaffoldData] = useState<
    IRequestStatus<ArsTableBody<IEditorV3>>
  >({
    requesting: false,
    error: false,
  });
  const [errorText, setErrorText] = useState<string | null>(null);

  const getScaffoldData = useEffectEvent(async (body: ArsTableBody<IEditorV3>) => {
    const request = await loadWrapper(
      getArsTableCalculation,
      body,
      requestingScaffoldData,
      setRequestingScaffoldData,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (request?.success) {
      setCalculatedTableRows(request.data);
      setErrorText(null);
    } else if (request?.success === false) {
      setErrorText(request.errorText);
    }
  });

  useEffect(() => {
    if (body) getScaffoldData(body);
  }, [body]);

  return !tlfContext || !body ? null : (
    <Container style={{ minHeight: "400px" }}>
      {isLoading ? (
        <Row>
          <Col>
            <PauseCover text="Loading data sets..." />
          </Col>
        </Row>
      ) : (
        <>
          <Row>
            <Col className="d-flex align-items-start flex-row gap-1 py-3">
              <ArsSetPicker
                type={"analysisMethod"}
                itemId={body.analysisMethodId}
                onChange={(newId) => {
                  tlfContext.dispatch({
                    operation: UPDATE_TLF,
                    tlfData: {
                      body: {
                        ...body,
                        analysisMethodId: newId,
                      },
                    },
                    calledFrom: "TlfArsTable onChange:AnalysisMethod",
                  });
                }}
              />
              <ArsSetPicker
                type={"analysisSet"}
                itemId={body.analysisSetId}
                onChange={(newId) => {
                  tlfContext.dispatch({
                    operation: UPDATE_TLF,
                    tlfData: {
                      body: {
                        ...body,
                        analysisSetId: newId,
                      },
                    },
                    calledFrom: "TlfArsTable onChange:AnalysisSet",
                  });
                }}
              />
              <ArsSetPicker
                type={"dataSubset"}
                itemId={body.dataSubsetId ?? ""}
                onChange={(newId) => {
                  tlfContext.dispatch({
                    operation: UPDATE_TLF,
                    tlfData: {
                      body: {
                        ...body,
                        dataSubsetId: newId,
                      },
                    },
                    calledFrom: "TlfArsTable onChange:DataSubset",
                  });
                }}
              />
            </Col>
          </Row>
          {errorText && (
            <Row>
              <Col className="text-danger m-3">{errorText}</Col>
            </Row>
          )}
          <Row>
            <Col className="d-flex justify-content-center position-relative">
              {requestingScaffoldData.requesting && <PauseCover text="Updating table..." />}
              <CommentScope
                id="ars-table-comment"
                activeContext="TlfItem"
                wrap
                iconPosition={{ top: "16px", left: "38px" }}
                commentLabel="New table comment"
              >
                <ArsTable<IEditorV3>
                  arsTableBody={body}
                  disabled={disabled}
                  editorProps={v3EditorProps}
                  calculatedTableRows={calculatedTableRows}
                  tlfViewMode={tlfContext.tlfViewMode}
                  updateArsTableBody={(newBody) =>
                    tlfContext!.dispatch({
                      operation: UPDATE_TLF,
                      tlfData: {
                        body: newBody as ArsTableBody<IEditorV3>,
                      },
                      calledFrom: "TlfArsTable onChange",
                    })
                  }
                />
              </CommentScope>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

TlfArsTable.displayName = "TlfArsTable";
